import { API_BASE_URL } from '../config/api';

// Default timeout for fetch requests (30 seconds)
const DEFAULT_FETCH_TIMEOUT_MS = 30000;

// Global auth error handler - will be set by AuthContext
let globalAuthErrorHandler = null;

export function setGlobalAuthErrorHandler(handler) {
  globalAuthErrorHandler = handler;
}

/**
 * Fetch with timeout support using AbortController
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Centralized API utility for making authenticated requests.
 *
 * Features:
 * - Automatic credential inclusion for httpOnly cookies
 * - Automatic token refresh on 401
 * - Consistent error handling
 * - Request/response transformation
 *
 * Usage:
 *   import { api } from '../utils/api';
 *
 *   // GET request
 *   const { data, error } = await api.get('/users');
 *
 *   // POST request
 *   const { data, error } = await api.post('/users', { name: 'John' });
 *
 *   // With custom options
 *   const { data } = await api.get('/users', { cache: 'no-store' });
 */

class ApiClient {
  constructor(baseUrl, defaultTimeout = DEFAULT_FETCH_TIMEOUT_MS) {
    this.baseUrl = baseUrl;
    this.refreshPromise = null;
    this.defaultTimeout = defaultTimeout;
  }

  /**
   * Build full URL from path
   */
  buildUrl(path, params = null) {
    const url = path.startsWith('http') ? path : `${this.baseUrl}${path}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      return queryString ? `${url}?${queryString}` : url;
    }
    return url;
  }

  /**
   * Refresh access token
   */
  async refreshToken() {
    // Deduplicate concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        console.log('[API] Attempting token refresh to:', `${this.baseUrl}/api/auth/refresh`);
        const res = await fetch(`${this.baseUrl}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        console.log('[API] Refresh response status:', res.status);
        if (res.ok) {
          return { ok: true };
        }
        const body = await res.json().catch(() => ({}));
        console.log('[API] Refresh failed:', body.code || body.error || 'unknown');
        return { ok: false, code: body.code || null };
      } catch (err) {
        console.log('[API] Refresh error:', err.message);
        return { ok: false, code: null };
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Make HTTP request
   */
  async request(method, path, data = null, options = {}) {
    const url = this.buildUrl(path, options.params);
    const isStateChanging = !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());

    // Build headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const fetchOptions = {
      method,
      credentials: 'include',
      headers,
      ...options,
    };

    // Add body for non-GET requests
    if (data && isStateChanging) {
      fetchOptions.body = JSON.stringify(data);
    }

    const makeRequest = async (isRetry = false) => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[API] ${method} ${url}`, { isRetry });
        }
        const timeout = options.timeout || this.defaultTimeout;
        const res = await fetchWithTimeout(url, fetchOptions, timeout);
        if (process.env.NODE_ENV === 'development') {
          console.log(`[API] Response: ${res.status}`);
        }

        // Handle 401 - try to refresh token
        if (res.status === 401 && !isRetry) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[API] Got 401, attempting token refresh...');
          }
          // Peek at the failing response's code so we can distinguish a
          // routine "access token expired" from a kicked session.
          const initialBody = await res
            .clone()
            .json()
            .catch(() => ({}));
          const refreshResult = await this.refreshToken();
          if (process.env.NODE_ENV === 'development') {
            console.log('[API] Refresh result:', refreshResult);
          }
          if (refreshResult.ok) {
            return makeRequest(true);
          }
          const kicked =
            refreshResult.code === 'SESSION_INVALIDATED' ||
            initialBody?.code === 'SESSION_INVALIDATED';
          if (globalAuthErrorHandler) {
            globalAuthErrorHandler(kicked ? { reason: 'session_invalidated' } : undefined);
          }
          return {
            data: null,
            error: kicked ? 'Сесію завершено: вхід з іншого пристрою' : 'Authentication failed',
            status: 401,
          };
        }

        // Parse response
        const contentType = res.headers.get('content-type');
        let responseData = null;

        if (contentType?.includes('application/json')) {
          responseData = await res.json();
        } else if (res.status !== 204) {
          responseData = await res.text();
        }

        if (!res.ok) {
          return {
            data: null,
            error: responseData?.error || responseData?.message || `Request failed with status ${res.status}`,
            status: res.status,
          };
        }

        return { data: responseData, error: null, status: res.status };
      } catch (err) {
        return {
          data: null,
          error: err.message || 'Network error',
          status: 0,
        };
      }
    };

    return makeRequest();
  }

  // Convenience methods
  async get(path, options = {}) {
    return this.request('GET', path, null, options);
  }

  async post(path, data, options = {}) {
    return this.request('POST', path, data, options);
  }

  async put(path, data, options = {}) {
    return this.request('PUT', path, data, options);
  }

  async delete(path, options = {}) {
    return this.request('DELETE', path, null, options);
  }

  async patch(path, data, options = {}) {
    return this.request('PATCH', path, data, options);
  }
}

// Create singleton instance
export const api = new ApiClient(API_BASE_URL);

/**
 * Pagination helper for API requests
 *
 * Usage:
 *   const { data, hasMore, nextPage } = await api.getPaginated('/users', { limit: 20 });
 */
export async function getPaginated(path, { page = 1, limit = 20, ...params } = {}) {
  const offset = (page - 1) * limit;
  const { data, error, status } = await api.get(path, {
    params: { ...params, limit, offset },
  });

  if (error) {
    return { data: null, error, status, hasMore: false, page };
  }

  // Try to extract items and total from response
  const items = Array.isArray(data) ? data : (data?.items || data?.data || []);
  const total = data?.total || data?.count || items.length;
  const hasMore = offset + items.length < total;

  return {
    data: items,
    total,
    page,
    limit,
    hasMore,
    nextPage: hasMore ? page + 1 : null,
    error: null,
    status,
  };
}

export default api;
